import { BodyText } from "@/components/ui/Text";
import { colors } from "@/constants/tokens/colors/colors";
import { memo, useCallback } from "react";
import type { DateGroup, SenderGroup } from "../hooks/useMessageGrouping";
import type { ChatMessage, ChatUserMap } from "../types/chatMessageTypes";
import { getUserDisplayName } from "../utils/chatSenderUtils";
import { DateSeparator } from "./DateSeparator";
import { MessageFactory } from "./Messages/MessageFactory";
import { TypingIndicator } from "./Messages/TypingIndicator";
import { UserAvatar } from "./UserAvatar";

// Constants
const LOADING_MORE_TEXT = "Loading more messages...";
const SYSTEM_TAG = "SYSTEM";
const CUSTOMER_TAG = "CUSTOMER";
const AUTOMATED_TAG = "AUTOMATED";
const DEFAULT_PATIENT_NAME = "Patient";
const DEFAULT_PROVIDER_NAME = "Provider";
const UNKNOWN_PREFIX = "unknown-";

/**
 * ChatMessageContent component
 *
 * Renders the actual content of the chat, including:
 * - Date separators to group messages by date
 * - Message grouping by sender (with avatar and name)
 * - System messages (displayed differently from user messages)
 * - Typing indicators
 * - Infinite scrolling loading state
 *
 * This component is responsible for the presentation logic of messages
 * but delegates actual message rendering to the MessageFactory component.
 */
interface ChatMessageContentProps {
  // The messages grouped by date and sender
  dateGroups: DateGroup[];
  // Map of user IDs to user data
  userMap: ChatUserMap;
  // Whether there are more messages to load
  hasNextPage: boolean;
  // Whether more messages are currently being loaded
  isFetchingNextPage: boolean;
  // Ref callback for the infinite scrolling trigger element
  loadMoreRef: (node: Element | null) => void;
  // Ref callback for detecting when the bottom of the chat is visible
  bottomRef: (node: Element | null) => void;
  // Whether a user is currently typing
  typingUser: boolean;
  // Whether a video call is pending
  isCallPending: boolean;
  // Function to toggle a video call
  toggleVideoVisitCall: (encounterId: string) => void;
  // The encounter ID
  encounterId?: string;
  // Patient's display name for fallback
  patientDisplayName?: string;
}

export const ChatMessageContent = memo(
  ({
    dateGroups,
    userMap,
    hasNextPage,
    isFetchingNextPage,
    loadMoreRef,
    bottomRef,
    typingUser,
    isCallPending,
    toggleVideoVisitCall,
    encounterId,
    patientDisplayName,
  }: ChatMessageContentProps) => {
    // Render a single message using the MessageFactory
    // Memoized to avoid recreating on each render
    const renderMessageGroup = useCallback(
      (message: ChatMessage, userMap: ChatUserMap) => (
        <MessageFactory
          key={message.uuid}
          message={message}
          userMap={userMap}
          isCallPending={isCallPending}
          toggleVideoVisitCall={toggleVideoVisitCall}
          encounterId={encounterId}
          patientDisplayName={patientDisplayName}
        />
      ),
      [encounterId, isCallPending, toggleVideoVisitCall, patientDisplayName],
    );

    // Render a group of messages from the same sender
    // Handles different rendering for system messages vs. user messages
    const renderSenderGroup = useCallback(
      (senderGroup: SenderGroup, index: number) => {
        const isSystemMessage = senderGroup.senderTag === SYSTEM_TAG;
        const isCustomerMessage = senderGroup.senderTag === CUSTOMER_TAG;
        const isAutomatedMessage = senderGroup.senderTag === AUTOMATED_TAG;

        // System messages don't get grouped visually with avatar, etc.
        if (isSystemMessage) {
          return (
            <div
              className="flex flex-col gap-3"
              key={`${senderGroup.senderId}-${index}`}
            >
              {senderGroup.messages.map((message) =>
                renderMessageGroup(message, userMap),
              )}
            </div>
          );
        }

        // For user messages, look up the user info from the user map
        const user = senderGroup.senderId.startsWith(UNKNOWN_PREFIX)
          ? undefined
          : userMap[senderGroup.senderId];

        // Get an appropriate display name for the sender
        const senderName = isAutomatedMessage
          ? "Curai Health"
          : user
            ? getUserDisplayName(user)
            : isCustomerMessage
              ? patientDisplayName || DEFAULT_PATIENT_NAME
              : DEFAULT_PROVIDER_NAME;

        return (
          <div
            key={`${senderGroup.senderId}-${index}`}
            className="flex flex-col gap-2 justify-end"
          >
            {/* Sender header with avatar */}
            <UserAvatar
              user={user}
              fallbackName={senderName}
              isCustomer={isCustomerMessage || isAutomatedMessage}
              bgColor={isAutomatedMessage ? colors.salmon[500] : undefined}
              fallbackInitials={isAutomatedMessage ? "CH" : undefined}
            />

            {/* Message bubbles */}
            <div className="flex flex-col items-start gap-3">
              {senderGroup.messages.map((message) =>
                renderMessageGroup(message, userMap),
              )}
            </div>
          </div>
        );
      },
      [renderMessageGroup, userMap, patientDisplayName],
    );
    return (
      <>
        {/* Typing indicator - shows when someone is typing */}
        {typingUser && (
          <div className="sticky bottom-0 bg-white">
            <TypingIndicator />
          </div>
        )}

        {/* Invisible element to detect when bottom is visible */}
        <div ref={bottomRef} className="h-1" />

        {/* Render each date group with its messages */}
        {dateGroups.map((dateGroup) => (
          <div key={dateGroup.date} className="">
            {/* Date separator - shows the date of the messages */}
            <DateSeparator date={dateGroup.date} />

            {/* Render all sender groups for this date */}
            <div className="flex flex-col gap-3">
              {dateGroup.senderGroups.map((senderGroup, index) =>
                renderSenderGroup(senderGroup, index),
              )}
            </div>
          </div>
        ))}

        {/* Infinite scrolling trigger - loads more messages when visible */}
        {hasNextPage && (
          <div
            ref={loadMoreRef}
            className="h-8 flex items-center justify-center"
          >
            {isFetchingNextPage && (
              <BodyText size="xs" textColor="tertiary">
                {LOADING_MORE_TEXT}
              </BodyText>
            )}
          </div>
        )}
      </>
    );
  },
);

ChatMessageContent.displayName = "ChatMessageContent";
